import React, { useCallback } from "react";
import { View, Text, StyleSheet } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import LHSectionHeader from "../../components/LHSectionHeader";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import SignaturePad from "../../components/SignaturePad";

// Import the action from Redux slice
import {
  resetSignatureUpdatedFlag,
  setSignatureFileName,
} from "../../store/slices/shiftHandOverSlice";
import { useTheme } from "../../theme";

const ShiftHandOverSignature: React.FC<{ forceDisabled?: boolean }> = ({
  forceDisabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const { signatureFileName, signatureImageUrl, handoverState, isValidating } =
    useSelector((state: RootState) => state.shiftHandOver);

  // Determine if component is disabled based on handover state or forceDisabled
  const isDisabled = handoverState === "completed" || forceDisabled;
  // Only show image URL if there's an actual signature file after upload
  // This ensures it's empty initially
  const showImageUrl = signatureFileName
    ? signatureImageUrl || signatureFileName
    : undefined;

  console.log("ShiftHandOverSignature: signature data from state:", {
    signatureFileName,
    signatureImageUrl,
    showImageUrl,
  });

  // Function to handle clearing signature data from Redux
  const handleClearSignature = useCallback(() => {
    console.log("ShiftHandOverSignature: Clearing signature data from Redux");
    dispatch(
      setSignatureFileName({
        fileName: null,
        imageUrl: null,
        isNewUpload: false,
      })
    );
    dispatch(resetSignatureUpdatedFlag());
  }, [dispatch]);

  return (
    <View
      style={[
        styles.cardContainer,
        isDarkMode && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
        },
      ]}
    >
      <LHSectionHeader
        title="Signature"
        containerStyle={
          isDarkMode
            ? { ...styles.shiftHeader, backgroundColor: colors.brand.primary }
            : styles.shiftHeader
        }
        textStyle={styles.sectionHeaderText}
        // icon={<MaterialIcons name="gesture" size={20} color="white" />}
      />
      <View
        style={[
          styles.signaturePadContainer,
          isDarkMode && { backgroundColor: colors.background.surface },
        ]}
      >
        {!signatureFileName && isValidating && (
          <Text style={styles.requiredText}>
            * Signature is required to complete the handover
          </Text>
        )}
        <SignaturePad
          containerName="shifthandover"
          onOK={(fileName: string, imageUrl?: string) => {
            if (isDisabled) return;
            dispatch(
              setSignatureFileName({
                fileName,
                imageUrl,
                isNewUpload: true,
              })
            );
            console.log(
              "ShiftHandOverSignature: Dispatched setSignatureFileName action with isNewUpload=true"
            );

            setTimeout(() => {
              const state = require("../../store").default.getState();
              console.log(
                "ShiftHandOverSignature: Redux state after dispatch -",
                "signatureFileName:",
                state.shiftHandOver.signatureFileName,
                "signatureImageUrl:",
                state.shiftHandOver.signatureImageUrl,
                "isSignatureUpdated:",
                state.shiftHandOver.isSignatureUpdated
              );
            }, 100);
          }}
          onClear={handleClearSignature}
          disabled={isDisabled}
          initialImageUrl={showImageUrl}
          isDarkTheme={isDarkMode}
          surfaceColor={isDarkMode ? colors.background.elevated : "#ffffff"}
          elevatedColor={isDarkMode ? colors.background.surface : "#D0D0D0"}
          borderColor={isDarkMode ? colors.border.default : "#C0C0C0"}
          textColor={isDarkMode ? colors.text.inverse : "#FFFFFF"}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e3e6ea',
    shadowColor: 'rgba(0, 94, 192, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
    marginBottom: 24,
    overflow: 'hidden',
  },
  shiftHeader: {
    backgroundColor: '#3582E6',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingVertical: 8,
    paddingHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionHeaderText: {
    fontSize: 16,
    color: 'white',
    fontWeight: 'bold',
    flex: 1,
  },
  signaturePadContainer: {
    borderWidth: 0,
    borderRadius: 0,
    padding: 16,
    backgroundColor: '#fff',
    height: 400,
  },
  signatureText: {
    marginTop: 8,
    fontSize: 14,
    color: '#495057',
    fontWeight: '500',
  },
  requiredText: {
    color: '#dc3545',
    fontSize: 14,
    marginBottom: 10,
    fontStyle: 'italic',
  },
});

export default ShiftHandOverSignature;
